// Merkan · Geführter Zugriff: Ebene mit Hervorhebung (Spotlight) und Karte.
// Ablauf je Schritt: Route prüfen oder wechseln, Ziel suchen (bis 4 s, Menü-Blatt bei Bedarf öffnen), ins Bild holen,
// Karte neben dem Ziel platzieren (bis 600 px als Blatt unten oder oben). Neu berechnet wird bei resize, scroll,
// Größenänderung von Ziel und Seite und bei DOM-Änderungen. Wechselt die Breite über 860 px, werden die
// mobil-Abweichungen des Schritts neu aufgelöst, der Schritt bleibt derselbe.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MousePointerClick } from 'lucide-react';
import { IcSchliessen as X } from '../ui/icons';
import { useSession } from '../lib/session';
import { t } from '../lib/sprache';
import { useAnleitungen, type Anleitung, type TourSchritt } from './anleitungen';
import {
  ereignisseVerbinden, pfadMerken, schrittSetzen, tourAbbrechen, tourAusblenden, tourBeenden, tourEinrichten, tourZustand, useTour,
  type AktiveTour,
} from './tour';
import './hilfe.css';

const MOBIL = '(max-width: 860px)';
const RAND = 16;
const ABSTAND = 12;
const POLSTER = 6;
const SUCHZEIT = 4000;

interface Rechteck { x: number; y: number; w: number; h: number }
interface Layout {
  /** Spotlight (mit Polster) oder null = alles abgedunkelt */
  loch: Rechteck | null;
  radius: number;
  /** Ausgesparte Fläche für Klicks (ohne Polster, auf den Sichtbereich begrenzt) */
  frei: Rechteck | null;
  karte: { art: 'schwebend' | 'unten' | 'oben' | 'mitte'; x: number; y: number };
}

/** Schritt mit mobil-Abweichungen für schmale Bildschirme */
function effektiv(s: TourSchritt, mobil: boolean): TourSchritt {
  if (!mobil || !s.mobil) return s;
  return { ...s, ziel: s.mobil.ziel ?? s.ziel, text: s.mobil.text ?? s.text, titel: s.mobil.titel ?? s.titel };
}

const istMuster = (r?: string | null) => Boolean(r && r.includes('*'));
const musterPasst = (muster: string, pfad: string) => {
  const re = new RegExp('^' + muster.split('*').map((x) => x.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('[^?#]+') + '/?$');
  return re.test(pfad);
};
const nurPfad = (p: string) => p.split(/[?#]/)[0];

/** Sichtbar und bedienbar: hat eine Fläche, ist nicht unsichtbar geschaltet */
function sichtbar(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return false;
  // Seitlich aus dem Bild geschoben (Menü-Blatt im Übergang, Karussell): nicht erreichbar
  const vw = document.documentElement.clientWidth;
  if (r.right <= 1 || r.left >= vw - 1) {
    // Seitlich in einem wischbaren Bereich (Reiter, Filterzeile, Tabelle): erreichbar, die Engine holt es ins Bild
    let p = el.parentElement;
    let wischbar = false;
    while (p && p !== document.body) {
      const ox = getComputedStyle(p).overflowX;
      if ((ox === 'auto' || ox === 'scroll') && p.scrollWidth > p.clientWidth + 1) {
        const pr = p.getBoundingClientRect();
        wischbar = pr.right > 1 && pr.left < vw - 1;
        break;
      }
      p = p.parentElement;
    }
    if (!wischbar) return false;
  }
  // Menü am Handy: nur bei offenem Blatt
  if (schmal() && !menueOffen() && el.closest('#hauptmenue')) return false;
  const cv = (el as Element & { checkVisibility?: (o?: object) => boolean }).checkVisibility;
  if (cv) return cv.call(el, { visibilityProperty: true, opacityProperty: false });
  const st = getComputedStyle(el);
  return st.visibility !== 'hidden' && st.display !== 'none';
}

function finden(selektor: string): { el: Element | null; imMenue: Element | null } {
  let liste: Element[] = [];
  try { liste = Array.from(document.querySelectorAll(selektor)); } catch { return { el: null, imMenue: null }; }
  liste = liste.filter((e) => !e.closest('.hi-ebene'));
  const el = liste.find(sichtbar) ?? null;
  return { el, imMenue: el ? null : liste.find((e) => e.closest('#hauptmenue')) ?? null };
}

const menueOffen = () => Boolean(document.querySelector('.rahmen.sheet-offen'));
const schmal = () => window.matchMedia(MOBIL).matches;
const bewegungAus = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Inline **fett** für Schritttexte */
function textMitFett(s: string): ReactNode[] {
  return s.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((teil, i) => (teil.startsWith('**') && teil.endsWith('**') ? <strong key={i}>{teil.slice(2, -2)}</strong> : teil));
}

function platzieren(loch: Rechteck | null, kw: number, kh: number): Layout['karte'] {
  const vw = document.documentElement.clientWidth;
  const vh = window.innerHeight;
  if (vw <= 600) {
    if (!loch) return { art: 'unten', x: 0, y: 0 };
    // Blatt dorthin, wo neben dem sichtbaren Teil des Ziels mehr Platz ist
    const oben = Math.max(0, loch.y);
    const unten = Math.max(0, vh - Math.min(vh, loch.y + loch.h));
    return { art: oben > unten ? 'oben' : 'unten', x: 0, y: 0 };
  }
  if (!loch) return { art: 'mitte', x: Math.round((vw - kw) / 2), y: Math.round(Math.max(RAND, (vh - kh) / 2)) };
  const begrenzen = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
  // Für die Platzierung zählt nur der sichtbare Teil des Ziels
  const l = Math.max(loch.x, 0), o = Math.max(loch.y, 0);
  const r = Math.min(loch.x + loch.w, vw), u = Math.min(loch.y + loch.h, vh);
  const cx = (l + r) / 2, cy = (o + u) / 2;
  const xMitte = begrenzen(cx - kw / 2, RAND, vw - RAND - kw);
  const yMitte = begrenzen(cy - kh / 2, RAND, vh - RAND - kh);
  const kandidaten: { x: number; y: number; passt: boolean; platz: number }[] = [
    { x: xMitte, y: u + ABSTAND, passt: u + ABSTAND + kh <= vh - RAND, platz: vh - u },
    { x: xMitte, y: o - ABSTAND - kh, passt: o - ABSTAND - kh >= RAND, platz: o },
    { x: r + ABSTAND, y: yMitte, passt: r + ABSTAND + kw <= vw - RAND, platz: vw - r },
    { x: l - ABSTAND - kw, y: yMitte, passt: l - ABSTAND - kw >= RAND, platz: l },
  ];
  const wahl = kandidaten.find((k) => k.passt);
  if (wahl) return { art: 'schwebend', x: Math.round(wahl.x), y: Math.round(wahl.y) };
  // Nichts passt (großes Ziel): unten rechts in der Ecke, vollständig sichtbar
  return { art: 'schwebend', x: Math.round(vw - RAND - kw), y: Math.round(vh - RAND - kh) };
}

function gleich(a: Layout | null, b: Layout): boolean {
  if (!a) return false;
  const r = (x: Rechteck | null, y: Rechteck | null) => (!x && !y) || Boolean(x && y && Math.abs(x.x - y.x) < 0.5 && Math.abs(x.y - y.y) < 0.5 && Math.abs(x.w - y.w) < 0.5 && Math.abs(x.h - y.h) < 0.5);
  return r(a.loch, b.loch) && r(a.frei, b.frei) && a.radius === b.radius && a.karte.art === b.karte.art && a.karte.x === b.karte.x && a.karte.y === b.karte.y;
}

// ---------------------------------------------------------------------------
// Einbindung im Rahmen: Fortschritt laden, Ereignisse verbinden, aktive Tour zeigen
// ---------------------------------------------------------------------------
export default function Tour() {
  const { mandant, session } = useSession();
  const mandantId = mandant?.mandant_id ?? null;
  const userId = session?.user.id ?? null;
  useEffect(() => { tourEinrichten(mandantId, userId); }, [mandantId, userId]);
  useEffect(() => ereignisseVerbinden(), []);
  const { aktiv } = useTour();
  const { liste } = useAnleitungen(mandantId);
  const anleitung = aktiv && liste ? liste.find((a) => a.id === aktiv.id) ?? null : null;
  const unbrauchbar = Boolean(aktiv && liste && (!anleitung || !anleitung.freigeschaltet || !anleitung.schritte.length));
  useEffect(() => { if (unbrauchbar) tourAusblenden(); }, [unbrauchbar]);
  if (!aktiv || !anleitung || unbrauchbar) return null;
  return createPortal(<TourEbene key={aktiv.id} anleitung={anleitung} aktiv={aktiv} />, document.body);
}

/** Pfade, auf denen die Schritte zuletzt gezeigt wurden (für „Zurück“ über Seitenwechsel hinweg) */
const besucht = new Map<string, Map<number, string>>();

function TourEbene({ anleitung, aktiv }: { anleitung: Anleitung; aktiv: AktiveTour }) {
  const navigate = useNavigate();
  const ort = useLocation();
  const anzahl = anleitung.schritte.length;
  const n = Math.min(aktiv.schritt, anzahl - 1);
  const [mobil, setMobil] = useState(schmal);
  const schritt = effektiv(anleitung.schritte[n], mobil);
  const letzter = n === anzahl - 1;

  const [phase, setPhase] = useState<'suchen' | 'gezeigt' | 'fehlt'>('suchen');
  const [ziel, setZiel] = useState<Element | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [neuAufloesen, setNeuAufloesen] = useState(0);
  const [wackeln, setWackeln] = useState(0);
  const [gleiten, setGleiten] = useState(false);

  const karteRef = useRef<HTMLDivElement>(null);
  const zielRef = useRef<Element | null>(null);
  const phaseRef = useRef(phase);
  const layoutRef = useRef<Layout | null>(null);
  const einsprungRef = useRef<number | null>(null); // lauf, dessen Einsprung schon erledigt ist
  zielRef.current = ziel;
  phaseRef.current = phase;

  // Breite über 860 px gewechselt: mobil-Abweichungen neu auflösen, Schritt bleibt
  useEffect(() => {
    const mq = window.matchMedia(MOBIL);
    const h = () => setMobil(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  // --- Layout berechnen (rAF-gedrosselt) ------------------------------------------------
  const rafRef = useRef(0);
  const berechnen = useCallback(() => {
    rafRef.current = 0;
    const el = zielRef.current;
    let loch: Rechteck | null = null, frei: Rechteck | null = null, radius = 10;
    if (el && el.isConnected) {
      const r = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth, vh = window.innerHeight;
      // Auf den Sichtbereich (mit Überstand für die Rundung) begrenzen: sehr hohe Ziele würden sonst eine riesige Ebene erzeugen
      const UE = 32;
      const lx = Math.max(r.left - POLSTER, -UE), ly = Math.max(r.top - POLSTER, -UE);
      const rx = Math.min(r.right + POLSTER, vw + UE), uy = Math.min(r.bottom + POLSTER, vh + UE);
      loch = { x: lx, y: ly, w: Math.max(0, rx - lx), h: Math.max(0, uy - ly) };
      const fl = Math.max(0, r.left), fo = Math.max(0, r.top);
      frei = { x: fl, y: fo, w: Math.max(0, Math.min(vw, r.right) - fl), h: Math.max(0, Math.min(vh, r.bottom) - fo) };
      const er = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
      radius = Math.round(Math.min(Math.max(er + POLSTER * 0.6, 8), loch.h / 2, loch.w / 2));
    }
    const k = karteRef.current;
    const karte = platzieren(loch, k?.offsetWidth ?? 340, k?.offsetHeight ?? 200);
    const neu: Layout = { loch, frei, radius, karte };
    if (!gleich(layoutRef.current, neu)) { layoutRef.current = neu; setLayout(neu); }
  }, []);
  const planen = useCallback(() => { if (!rafRef.current) rafRef.current = requestAnimationFrame(berechnen); }, [berechnen]);
  /** Für eine kurze Zeit jeden Frame nachführen (Übergänge wie das Menü-Blatt, sanftes Scrollen) */
  const nachfuehren = useCallback((ms: number) => {
    const ende = performance.now() + ms;
    const tick = () => { berechnen(); if (performance.now() < ende) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }, [berechnen]);

  // --- Schritt auflösen: Route, Ziel suchen, ins Bild holen ---------------------------------
  useEffect(() => {
    let abgebrochen = false;
    const aufraeumen: (() => void)[] = [];
    const warte = (ms: number) => new Promise<void>((res) => { const id = window.setTimeout(res, ms); aufraeumen.push(() => window.clearTimeout(id)); });
    const pfadJetzt = () => window.location.pathname;

    (async () => {
      setPhase('suchen');
      setZiel(null);
      const s = schritt;
      // Auf breiten Bildschirmen gibt es kein Menü-Blatt: ein offenes (z. B. nach dem Drehen) schließen
      if (!schmal() && menueOffen()) window.dispatchEvent(new Event('merkan:menue-schliessen'));
      // Erst nach einem vollständigen Lauf als erledigt merken (StrictMode und Abbrüche starten den Lauf neu)
      const einsprung = aktiv.einsprung && einsprungRef.current !== aktiv.lauf;
      const gespeichert = einsprung ? aktiv.pfad : null;
      let routeFest = s.route && !istMuster(s.route) ? s.route : null;
      // Erster Schritt ohne eigene Route: Startseite der Anleitung
      if (!routeFest && !s.route && n === 0 && einsprung && !gespeichert && anleitung.route && !istMuster(anleitung.route)) routeFest = anleitung.route;
      const muster = istMuster(s.route) ? s.route! : null;

      if (gespeichert && (!routeFest || nurPfad(gespeichert) === routeFest) && (!muster || musterPasst(muster, nurPfad(gespeichert)))) {
        if (pfadJetzt() + window.location.search !== gespeichert) navigate(gespeichert);
      } else if (routeFest) {
        if (pfadJetzt() !== routeFest) navigate(routeFest);
      } else if (!muster && aktiv.richtung === -1) {
        // Zurück auf einen Schritt ohne Route: dorthin, wo er zuletzt zu sehen war
        const war = besucht.get(anleitung.id)?.get(n);
        if (war && pfadJetzt() + window.location.search !== war) navigate(war);
      }

      if (muster) {
        let passt = false;
        for (let i = 0; i < 15 && !abgebrochen; i++) { if (musterPasst(muster, pfadJetzt())) { passt = true; break; } await warte(100); }
        if (abgebrochen) return;
        if (!passt) {
          const merk = tourZustand().fortschritt[anleitung.id]?.pfad ?? null;
          const kandidat = [gespeichert, merk, besucht.get(anleitung.id)?.get(n)].find((p): p is string => Boolean(p && musterPasst(muster, nurPfad(p))));
          if (kandidat) {
            navigate(kandidat);
            for (let i = 0; i < 15 && !abgebrochen && !musterPasst(muster, pfadJetzt()); i++) await warte(100);
          } else {
            // Zurück zum Schritt, der hierher führt (Klick-Schritt oder feste Route davor)
            let k = n - 1;
            while (k > 0) {
              const v = effektiv(anleitung.schritte[k], schmal());
              if (v.aktion === 'klick' || (v.route && !istMuster(v.route))) break;
              k--;
            }
            if (!abgebrochen) schrittSetzen(Math.max(0, k));
            return;
          }
        }
      }
      if (abgebrochen) return;

      if (!s.ziel) {
        if (menueOffen()) window.dispatchEvent(new Event('merkan:menue-schliessen'));
        setZiel(null);
        setPhase('gezeigt');
        return;
      }

      // Ziel suchen: Polling 100 ms und DOM-Beobachtung, höchstens 4 s
      const start = performance.now();
      let letzteAenderung = start;
      let menueAngefragt = 0;
      const mo = new MutationObserver(() => { letzteAenderung = performance.now(); });
      mo.observe(document.body, { childList: true, subtree: true, attributes: true });
      aufraeumen.push(() => mo.disconnect());
      let gefunden: Element | null = null;
      while (!abgebrochen) {
        const { el, imMenue } = finden(s.ziel);
        if (el) {
          if (!el.closest('#hauptmenue') && menueOffen() && schmal()) window.dispatchEvent(new Event('merkan:menue-schliessen'));
          gefunden = el; break;
        }
        // Menü-Blatt öffnen; schließt es sich wieder (z. B. durch den Seitenwechsel davor), erneut anfragen
        if (imMenue && schmal() && !menueOffen() && performance.now() - menueAngefragt > 700) {
          menueAngefragt = performance.now();
          window.dispatchEvent(new Event('merkan:menue-oeffnen'));
        }
        const jetzt = performance.now();
        // Optionale Schritte: aufgeben, sobald die Seite eine Weile ruhig ist (frühestens nach 1,2 s)
        if (s.optional && jetzt - start > 1200 && jetzt - letzteAenderung > 600) break;
        if (jetzt - start > SUCHZEIT) break;
        await warte(100);
      }
      mo.disconnect();
      if (abgebrochen) return;

      if (!gefunden && einsprung) {
        // Nach Neuladen oder Fortsetzen fehlt Zustand, den Klick-Schritte davor erzeugt haben (Panel, Reiter, Palette).
        // Diese Klicks sind laut Vorgabe harmlos (Öffnen, Wechseln): selbst wiederholen und genau hier weitermachen.
        // Gelingt das nicht, zurück zum ersten dieser Klick-Schritte, sofern keine feste Route dazwischen liegt.
        const kette: number[] = [];
        for (let k = n - 1; k >= 0; k--) {
          const v = effektiv(anleitung.schritte[k], schmal());
          if (v.aktion === 'klick') { kette.unshift(k); continue; }
          if (v.route && !istMuster(v.route)) break;
          if (kette.length) break;
        }
        for (const k of kette) {
          const v = effektiv(anleitung.schritte[k], schmal());
          let el: Element | null = null;
          for (let i = 0; i < 20 && !abgebrochen && !el; i++) { el = finden(v.ziel!).el; if (!el) await warte(100); }
          if (abgebrochen) return;
          if (el) { (el as HTMLElement).click(); await warte(450); }
        }
        if (kette.length) {
          for (let i = 0; i < 25 && !abgebrochen && !gefunden; i++) { gefunden = finden(s.ziel).el; if (!gefunden) await warte(100); }
          if (abgebrochen) return;
          if (!gefunden) { schrittSetzen(kette[0]); return; }
        }
      }
      if (!gefunden) {
        if (s.optional) {
          const ziel = aktiv.richtung === -1 && n > 0 ? n - 1 : n + 1;
          if (ziel < anzahl) { schrittSetzen(ziel); return; }
        }
        if (menueOffen() && schmal()) window.dispatchEvent(new Event('merkan:menue-schliessen'));
        setZiel(null);
        setPhase('fehlt');
        return;
      }

      // Ins Bild holen, wenn das Ziel nicht bequem sichtbar ist
      const r = gefunden.getBoundingClientRect();
      const oben = gefunden.closest('#hauptmenue') ? 8 : 64;
      const unten = window.innerHeight - (schmal() ? 84 : 16);
      const seitlich = r.left < 0 || r.right > document.documentElement.clientWidth;
      if (r.top < oben || r.bottom > unten || seitlich) {
        // Höher als der freie Bereich: Anfang des Ziels zeigen statt seiner Mitte
        const hoch = r.height > unten - oben - 40;
        if (hoch && r.top >= oben && r.top < unten - 160) { /* Anfang ist schon gut sichtbar */ }
        else {
          // Unter der festen Kopfleiste beginnen
          const el = gefunden as HTMLElement;
          const vorher = el.style.scrollMarginTop;
          if (hoch) el.style.scrollMarginTop = `${oben + 8}px`;
          el.scrollIntoView({ block: hoch ? 'start' : 'center', inline: 'center', behavior: bewegungAus() ? 'auto' : 'smooth' });
          if (hoch) window.setTimeout(() => { el.style.scrollMarginTop = vorher; }, 900);
        }
      }
      setZiel(gefunden);
      setPhase('gezeigt');
    })().then(() => { if (!abgebrochen) einsprungRef.current = aktiv.lauf; });

    return () => { abgebrochen = true; aufraeumen.forEach((f) => f()); };
    // Neu auflösen bei Schrittwechsel (lauf), Wechsel über 860 px oder angefordert
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktiv.lauf, n, mobil, neuAufloesen]);

  // Aktives Ziel markieren (für Tests und Bedienhilfen); entfernt sich beim Wechsel selbst
  useEffect(() => {
    if (!ziel || phase !== 'gezeigt') return;
    ziel.setAttribute('data-tour-ziel-aktiv', '');
    return () => ziel.removeAttribute('data-tour-ziel-aktiv');
  }, [ziel, phase]);

  // Gleiten der Karte nur beim Wechsel des Schritts, nicht beim Scrollen
  useEffect(() => {
    if (phase !== 'gezeigt' && phase !== 'fehlt') return;
    setGleiten(true);
    const id = window.setTimeout(() => setGleiten(false), 280);
    return () => window.clearTimeout(id);
  }, [phase, ziel, aktiv.lauf]);

  // Pfad merken, solange der Schritt gezeigt wird (für Fortsetzen und Neuladen)
  useEffect(() => {
    if (phase !== 'gezeigt' && phase !== 'fehlt') return;
    const p = ort.pathname + ort.search;
    pfadMerken(p);
    let m = besucht.get(anleitung.id);
    if (!m) { m = new Map(); besucht.set(anleitung.id, m); }
    m.set(n, p);
  }, [phase, ort.pathname, ort.search, anleitung.id, n]);

  // Seitenwechsel während des Schritts (z. B. Zurück im Browser): Ziel weg, also neu auflösen
  useEffect(() => {
    if (phaseRef.current !== 'gezeigt') return;
    const el = zielRef.current;
    if (el && !el.isConnected) setNeuAufloesen((x) => x + 1);
  }, [ort.pathname]);

  // --- Nachführen: resize, scroll, Größe von Ziel und Seite, DOM-Änderungen -----------------
  useLayoutEffect(() => { berechnen(); }, [berechnen, ziel, phase, n, mobil]);
  /** Ziel neu gezeichnet oder unsichtbar geworden: gleichen Selektor wieder suchen, sonst Schritt neu auflösen */
  const zielVerloren = useCallback((el: Element, selektor: string): boolean => {
    if (el.isConnected && sichtbar(el)) return false;
    const { el: neu } = finden(selektor);
    if (neu) { if (neu !== el) setZiel(neu); return neu !== el; }
    setNeuAufloesen((x) => x + 1);
    return true;
  }, []);
  // Bei Größenänderung zusätzlich prüfen, ob das Ziel noch erreichbar ist (Breakpoints blenden Elemente aus)
  const groesse = useCallback(() => {
    const el = zielRef.current;
    if (phaseRef.current === 'gezeigt' && el && schritt.ziel) {
      window.setTimeout(() => {
        const e = zielRef.current;
        if (!e || phaseRef.current !== 'gezeigt' || !schritt.ziel || zielVerloren(e, schritt.ziel)) return;
        // Nach dem Wechsel der Breite kann das Ziel außerhalb des Bildes liegen: wieder holen
        const r = e.getBoundingClientRect();
        const vw = document.documentElement.clientWidth, vh = window.innerHeight;
        if (r.bottom < 64 || r.top > vh - (schmal() ? 84 : 16) || r.right < 0 || r.left > vw) {
          e.scrollIntoView({ block: 'center', inline: 'center', behavior: 'auto' });
          planen();
        }
      }, 300);
    }
    planen();
  }, [planen, schritt.ziel, zielVerloren]);

  // Verschiebt sich das Ziel durch nachladende Inhalte (Bilder, Mails im Original, Blöcke einer Akte) aus dem Bild,
  // ohne dass der Benutzer selbst gescrollt hat, wird es wieder geholt (gedrosselt, höchstens alle 250 ms)
  const nutzerScrolltRef = useRef(false);
  const holenRef = useRef(0);
  useEffect(() => { nutzerScrolltRef.current = false; }, [ziel, aktiv.lauf]);
  const nachholen = useCallback(() => {
    if (holenRef.current) return;
    holenRef.current = window.setTimeout(() => {
      holenRef.current = 0;
      const el = zielRef.current as HTMLElement | null;
      if (!el || phaseRef.current !== 'gezeigt' || nutzerScrolltRef.current || !el.isConnected) return;
      const r = el.getBoundingClientRect();
      const oben = el.closest('#hauptmenue') ? 8 : 64;
      const unten = window.innerHeight - (schmal() ? 84 : 16);
      const vw = document.documentElement.clientWidth;
      const hoch = r.height > unten - oben - 40;
      const ausserhalb = hoch ? (r.top > unten - 120 || r.bottom < oben + 60) : (r.top < oben || r.bottom > unten);
      if (!ausserhalb && r.right > 0 && r.left < vw) return;
      el.scrollIntoView({ block: hoch ? 'start' : 'center', inline: 'center', behavior: 'auto' });
      planen();
    }, 250);
  }, [planen]);
  useEffect(() => {
    const nutzer = () => { nutzerScrolltRef.current = true; };
    const taste = (e: KeyboardEvent) => { if (/^(PageUp|PageDown|Home|End|ArrowUp|ArrowDown| )$/.test(e.key)) nutzer(); };
    window.addEventListener('wheel', nutzer, { passive: true });
    window.addEventListener('touchmove', nutzer, { passive: true });
    window.addEventListener('keydown', taste);
    return () => {
      window.removeEventListener('wheel', nutzer); window.removeEventListener('touchmove', nutzer); window.removeEventListener('keydown', taste);
      if (holenRef.current) { window.clearTimeout(holenRef.current); holenRef.current = 0; }
    };
  }, []);

  useEffect(() => {
    nachfuehren(450);
    const ro = new ResizeObserver(() => { planen(); nachholen(); });
    ro.observe(document.body);
    if (ziel) ro.observe(ziel);
    if (karteRef.current) ro.observe(karteRef.current);
    const mo = new MutationObserver((eintraege) => {
      if (eintraege.every((e) => (e.target as Element).closest?.('.hi-ebene'))) return;
      const el = zielRef.current;
      const p = phaseRef.current;
      if (p === 'gezeigt' && el && schritt.ziel && zielVerloren(el, schritt.ziel)) return;
      if (p === 'fehlt' && schritt.ziel) {
        const { el: neu } = finden(schritt.ziel);
        if (neu) { setZiel(neu); setPhase('gezeigt'); return; }
      }
      planen();
      if (p === 'gezeigt') nachholen();
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-expanded'] });
    const ende = () => nachfuehren(60);
    window.addEventListener('resize', groesse);
    window.addEventListener('scroll', planen, true);
    document.addEventListener('transitionend', ende, true);
    document.addEventListener('animationend', ende, true);
    window.visualViewport?.addEventListener('resize', planen);
    return () => {
      ro.disconnect(); mo.disconnect();
      window.removeEventListener('resize', groesse);
      window.removeEventListener('scroll', planen, true);
      document.removeEventListener('transitionend', ende, true);
      document.removeEventListener('animationend', ende, true);
      window.visualViewport?.removeEventListener('resize', planen);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    };
  }, [ziel, schritt.ziel, planen, nachfuehren, groesse, zielVerloren, nachholen]);

  // --- Aktionen --------------------------------------------------------------------------
  const weiter = useCallback(() => {
    if (letzter) {
      if (menueOffen()) window.dispatchEvent(new Event('merkan:menue-schliessen'));
      tourBeenden();
    } else schrittSetzen(n + 1);
  }, [letzter, n]);
  const zurueck = useCallback(() => { if (n > 0) schrittSetzen(n - 1); }, [n]);
  const abbrechen = useCallback(() => {
    if (menueOffen() && schmal()) window.dispatchEvent(new Event('merkan:menue-schliessen'));
    tourAbbrechen();
  }, []);

  // Klick-Schritt: Klick auf das Ziel führt weiter (Listener in der Capture-Phase am Ziel)
  const klickSchritt = schritt.aktion === 'klick' && phase === 'gezeigt' && Boolean(ziel);
  useEffect(() => {
    if (!klickSchritt || !ziel) return;
    let fertig = false;
    const h = () => {
      if (fertig) return;
      fertig = true;
      // Erst die eigentliche Aktion des Ziels laufen lassen (Navigation, Panel öffnen)
      window.setTimeout(() => { if (tourZustand().aktiv?.lauf === aktiv.lauf) weiter(); }, 0);
    };
    ziel.addEventListener('click', h, true);
    return () => ziel.removeEventListener('click', h, true);
  }, [klickSchritt, ziel, weiter, aktiv.lauf]);

  // Tastatur: Pfeil rechts und links, Esc bricht ab; Tab bleibt in der Karte
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const ziel = e.target as HTMLElement | null;
      const eingabe = Boolean(ziel && (ziel.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(ziel.tagName)));
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); abbrechen(); return; }
      if (eingabe && !karteRef.current?.contains(ziel)) return;
      if (e.key === 'ArrowRight' && !e.altKey && !e.metaKey && !e.ctrlKey) { e.preventDefault(); e.stopImmediatePropagation(); weiter(); }
      else if (e.key === 'ArrowLeft' && !e.altKey && !e.metaKey && !e.ctrlKey) { e.preventDefault(); e.stopImmediatePropagation(); zurueck(); }
      else if (e.key === 'Tab' && karteRef.current) {
        const fokusierbar = Array.from(karteRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), [href]'));
        if (!fokusierbar.length) return;
        const innen = karteRef.current.contains(document.activeElement);
        const erstes = fokusierbar[0], letztes = fokusierbar[fokusierbar.length - 1];
        if (!innen) { e.preventDefault(); erstes.focus(); }
        else if (e.shiftKey && document.activeElement === erstes) { e.preventDefault(); letztes.focus(); }
        else if (!e.shiftKey && document.activeElement === letztes) { e.preventDefault(); erstes.focus(); }
      }
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [weiter, zurueck, abbrechen]);

  // Fokus in die Karte, sobald der Schritt steht
  useEffect(() => {
    if (phase === 'suchen') return;
    const a = document.activeElement as HTMLElement | null;
    if (ziel && a && ziel.contains(a)) return; // Benutzer arbeitet gerade im Ziel
    karteRef.current?.focus({ preventScroll: true });
  }, [phase, aktiv.lauf, ziel]);

  // Klick auf die Abdunklung: nichts tun außer kurz wackeln
  const gesperrt = (e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); setWackeln((x) => x + 1); };

  const L = layout;
  const frei = phase === 'gezeigt' ? L?.frei ?? null : null;
  const loch = phase === 'gezeigt' ? L?.loch ?? null : null;
  const kartenArt = L?.karte.art ?? 'mitte';
  const vw = typeof document !== 'undefined' ? document.documentElement.clientWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const sperren: Rechteck[] = frei && frei.w > 0 && frei.h > 0
    ? [
      { x: 0, y: 0, w: vw, h: frei.y },
      { x: 0, y: frei.y + frei.h, w: vw, h: Math.max(0, vh - frei.y - frei.h) },
      { x: 0, y: frei.y, w: frei.x, h: frei.h },
      { x: frei.x + frei.w, y: frei.y, w: Math.max(0, vw - frei.x - frei.w), h: frei.h },
    ]
    : [{ x: 0, y: 0, w: vw, h: vh }];

  const titelId = `hi-titel-${anleitung.id}`;
  const textId = `hi-text-${anleitung.id}`;

  return (
    <div className="hi-ebene" data-phase={phase} data-anleitung={anleitung.id} data-schritt={n} data-anzahl={anzahl} data-klick={klickSchritt ? '1' : undefined}>
      <div
        className={`hi-spot${loch ? ' hi-spot-ziel' : ''}`}
        style={loch
          ? { transform: `translate3d(${loch.x}px, ${loch.y}px, 0)`, width: loch.w, height: loch.h, borderRadius: L?.radius }
          : { transform: `translate3d(${vw / 2}px, ${vh / 2}px, 0)`, width: 0, height: 0 }}
        aria-hidden="true"
      >
        {loch && <span key={`${aktiv.lauf}-${phase}`} className="hi-spot-ring" style={{ borderRadius: L?.radius }} />}
      </div>
      {sperren.map((s, i) => (
        <div key={i} className="hi-sperre" style={{ transform: `translate3d(${s.x}px, ${s.y}px, 0)`, width: s.w, height: s.h }}
          onPointerDown={gesperrt} onClick={gesperrt} onContextMenu={gesperrt} aria-hidden="true" />
      ))}
      <div
        className={`hi-karte-huelle hi-${kartenArt}${gleiten ? ' hi-gleiten' : ''}${phase === 'suchen' ? ' hi-suchen' : ''}`}
        style={kartenArt === 'schwebend' || kartenArt === 'mitte' ? { transform: `translate3d(${L?.karte.x ?? 0}px, ${L?.karte.y ?? 0}px, 0)` } : undefined}
      >
        <div
          ref={karteRef}
          key={`w${wackeln}`}
          className={`hi-karte${wackeln ? ' hi-wackeln' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titelId}
          aria-describedby={textId}
          tabIndex={-1}
          data-tour-karte=""
        >
          <div className="hi-karte-kopf">
            <span className="hi-zaehler">{t('Schritt {n} von {m}', { n: n + 1, m: anzahl })}</span>
            <span className="hi-anleitung" title={anleitung.titel}>{anleitung.titel}</span>
            <button type="button" className="hi-x" onClick={abbrechen} aria-label={t('Geführten Zugriff unterbrechen')} title={t('Unterbrechen (Esc)')}><X size={16} /></button>
          </div>
          <h2 id={titelId} className="hi-karte-titel">{schritt.titel}</h2>
          <p id={textId} className="hi-karte-text" aria-live="polite">{textMitFett(schritt.text)}</p>
          {phase === 'fehlt' && <p className="hi-karte-fehlt">{t('Dieses Element ist hier gerade nicht zu sehen.')}</p>}
          {klickSchritt && (
            <p className="hi-karte-klick"><MousePointerClick size={16} aria-hidden="true" />{t('Tippe auf das markierte Element')}</p>
          )}
          <div className="hi-karte-fuss">
            <div className="hi-punkte" aria-hidden="true">
              {anleitung.schritte.map((_, i) => <span key={i} className={i === n ? 'hi-punkt-aktiv' : i < n ? 'hi-punkt-erledigt' : undefined} />)}
            </div>
            <div className="hi-knoepfe">
              {n > 0 && <button type="button" className="kn kn-leise kn-klein" onClick={zurueck}><ArrowLeft />{t('Zurück')}</button>}
              {klickSchritt ? (
                <button type="button" className="kn kn-leise kn-klein" onClick={weiter} data-tour-weiter="">{letzter ? t('Fertig') : t('Weiter')}</button>
              ) : (
                <button type="button" className="kn kn-primaer kn-klein" onClick={weiter} data-tour-weiter="">
                  {letzter ? <><Check />{t('Fertig')}</> : <>{t('Weiter')}<ArrowRight /></>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
